import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { RadioControl } from '../../models/form/FormControlRadio';
import { createControl } from '../../utilities/formUtilities';
import { CardControl } from '../../../shared/models/CardControl';
import { StandingAuthorizationCard } from '../../../shared/components/standing-authorization-card-container/standing-authorization-card-container.component';
import { selectEnableUseSloaContainer } from '../../../feature-flags/reducers';
import { Store } from '@ngrx/store';

@Component({
  standalone: false,
  selector: 'mm-wire-standing-auth',
  templateUrl: './wire-standing-auth.component.html',
  styleUrls: ['./wire-standing-auth.component.scss'],
})
export class WireStandingAuthComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() standingAuthorizations$: Observable<StandingInstruction[] | null>;
  @Input() standingAuthSelection: string;
  private readonly formControlName: string = 'standingAuthId'
  showStandingAuthWarning: boolean;
  destroy$ = new Subject<boolean>();
  standingAuthCards: StandingAuthorizationCard[] = [];
  bundledStandingAuthCards: StandingAuthorizationCard[] = [];
  enableUseSloaContainer: boolean = false;
  bundledCardModel: CardControl = {
    title: 'Select a bundled standing authorization profile',
    controlName: this.formControlName,
    radios: [],
    validationMessage: 'Select a bundled authorization',
  };

  standingAuthCardModel: CardControl = {
    title: 'Select a standing authorization',
    controlName: this.formControlName,
    radios: [],
    validationMessage: 'Select a standing authorization',
  };

  constructor(private fb: UntypedFormBuilder, private store: Store) {}

  ngOnInit(): void {
    createControl(this.parentForm, this.formControlName, '', [Validators.required]);

    this.store.select(selectEnableUseSloaContainer).pipe(
      takeUntil(this.destroy$),
    ).subscribe((enableUseSloaContainer) => {
      this.enableUseSloaContainer = enableUseSloaContainer;
    });

    this.standingAuthorizations$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((standingAuthorizations) => {
      this.showStandingAuthWarning = false;
      if (!standingAuthorizations || standingAuthorizations.length === 0) {
        this.showStandingAuthWarning = true;
      }

      this.bundledCardModel.radios = standingAuthorizations?.map((auth) => {
        const label = [
          {text: 'To: ' + auth.to1},
          {text: auth.nickname},
          {text: auth.to3},
          {text: auth.isThirdParty ? 'Third party' : 'First party'},
        ];

        return {
          value: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
          label
        }
      }) ?? [];

      this.standingAuthCardModel.radios = standingAuthorizations?.map((auth) => {
        let label = [
          {text: auth.to1},
          {label: 'Account #: ', text: auth.to3, isNewGrid: true},
          {label: 'Profile type: ', text: (auth.isThirdParty ? 'Third party' : 'First party'), isNewGrid: true},
        ];
        if (auth.nickname) {
          label.unshift({text: auth.nickname}); //add nickname as first item in label if present
        }

        return {
          value: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
          label
        }
      }) ?? [];
      if (this.enableUseSloaContainer && standingAuthorizations && standingAuthorizations.length > 0) {
        this.updateStandingAuthorizationCards(standingAuthorizations);
      }
    })

  }

  private updateStandingAuthorizationCards(standingAuthorizations: StandingInstruction[] | null): void {
    this.bundledStandingAuthCards = standingAuthorizations?.map((auth) => ({
      id: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
      title: auth.to1 ? `To: ${auth.to1}` : '',
      subtitle: auth.nickname || '',
      details: [auth.to3, auth.isThirdParty ? 'Third party' : 'First party'].filter(Boolean) as string[],
      selected: this.standingAuthIdControl?.value === `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`
    })) ?? [];

    this.standingAuthCards = standingAuthorizations?.map((auth) => ({
      id: `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`,
      title: auth.nickname || auth.to1 || '',
      subtitle: auth.nickname ? auth.to1 : '',
      details: [
        auth.to3 ? `Account #: ${auth.to3}` : '',
        `Profile type: ${auth.isThirdParty ? 'Third party' : 'First party'}`
      ].filter(Boolean),
      selected: this.standingAuthIdControl?.value === `${auth.transactionType[0]}-${auth.cashieringRqstId}-${auth.cashieringRqstSecondId}`
    })) ?? [];
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl(this.formControlName);

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get standingAuthIdControl(): UntypedFormControl {
    return this.parentForm?.get(this.formControlName) as UntypedFormControl;
  }

  onStandingAuthCardSelected(cardId: string): void {
    this.standingAuthIdControl?.setValue(cardId);
    this.standingAuthIdControl?.markAsTouched();
    this.standingAuthCards = this.standingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
    this.bundledStandingAuthCards = this.bundledStandingAuthCards.map(card => ({ ...card, selected: card.id === cardId }));
  }

}
